import { useState, type FormEvent } from 'react'
import { supabase } from '../lib/supabase'
import { BrandMark } from './BrandMark'

type MfaChallengeScreenProps = {
  onVerified: () => Promise<void> | void
}

export function MfaChallengeScreen({ onVerified }: MfaChallengeScreenProps) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function verify(event: FormEvent) {
    event.preventDefault()
    setError(null)

    if (!/^\d{6}$/.test(code.trim())) {
      setError('Enter the six-digit code from your authenticator app.')
      return
    }

    setBusy(true)

    try {
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors()
      if (factorsError) throw factorsError

      const factor = factors?.totp?.find((item) => item.status === 'verified')
      if (!factor) {
        throw new Error('No verified authenticator app is available for this account.')
      }

      const { error: verificationError } = await supabase.auth.mfa.challengeAndVerify({
        factorId: factor.id,
        code: code.trim(),
      })
      if (verificationError) throw verificationError

      await onVerified()
    } catch (verificationError) {
      setError(
        verificationError instanceof Error
          ? verificationError.message
          : 'The authenticator code could not be verified.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="login-page">
      <section className="login-panel">
        <BrandMark />
        <div className="login-panel__copy">
          <span className="eyebrow">Protected manager access</span>
          <h1>One final security check.</h1>
          <p>
            Compliance management actions require your authenticator code before the control room opens.
          </p>
        </div>
        <div className="login-panel__features" aria-hidden="true">
          <span>Role checked</span>
          <span>MFA protected</span>
          <span>Audit ready</span>
        </div>
      </section>

      <section className="login-card" aria-label="Verify authenticator code">
        <div>
          <span className="eyebrow">Two-step verification</span>
          <h2>Enter your authenticator code</h2>
          <p>Open your authenticator app and enter the current six-digit VenTuhi code.</p>
        </div>

        <form className="form-stack" onSubmit={verify}>
          <label>
            Authenticator code
            <input
              type="text"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              autoComplete="one-time-code"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              required
            />
          </label>
          {error && <div className="message message--error">{error}</div>}
          <button className="button button--primary button--large" disabled={busy}>
            {busy ? 'Verifying…' : 'Open Compliance'}
          </button>
        </form>

        <button className="text-link" type="button" onClick={() => void supabase.auth.signOut()}>
          Sign in with a different account
        </button>
      </section>
    </main>
  )
}
